import { IShaderProgramInfo, initShaderProgram } from "../Shader";

import { mat4 } from "gl-matrix";

import { Camera } from "../Camera";

import { IRGBA } from "src/pages/FlightTimeline/TimelineUtils";

interface IGLAttributes {
  position: WebGLBuffer;
}

export class ScrollBarThumb {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform vec4 uColor;
    uniform float uYLoaded;
   
    varying lowp vec4 vColor;

    void main(void) {
      float newY = aVertexPosition.y + (uYLoaded * -2.0);
      float newZ = 0.0;
      vec4 position = vec4(aVertexPosition.x, newY, newZ, aVertexPosition.w );
      gl_Position =   position;
      vColor = uColor;

    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying lowp vec4 vColor;

    void main(void) {
      gl_FragColor = vColor;
    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;
  private buffers: IGLAttributes;
  private positions: number[] = [];

  private loaded!: number;

  private modelViewMatrix: mat4;
  constructor(gl: WebGLRenderingContext) {
    // Initialize a shader program; this is where all the lighting
    // for the vertices and so forth is established.
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);

    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        yLoaded: gl.getUniformLocation(this.shaderProgram, "uYLoaded"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };

    this.modelViewMatrix = mat4.create();
    mat4.translate(
      this.modelViewMatrix, // destination matrix
      this.modelViewMatrix, // matrix to translate
      [0.0, 0.0, -4.0]
    ); // amount to translate

    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  private initBuffers = (gl: WebGLRenderingContext): IGLAttributes => {
    // Create a buffer for the square's positions.
    const positionBuffer = gl.createBuffer();
    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    this.positions = [];

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.STATIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  public updateBuffers = (gl: WebGLRenderingContext, positions: number[]): void => {
    const positionBuffer = this.buffers.position;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);
  };

  public drawScene = (gl: WebGLRenderingContext, camera: Camera, color: IRGBA, maxHeight: number): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.

    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;

      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
      gl.vertexAttribPointer(
        this.programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
    }

    // -- How to calculate the scroll --
    const [yStart] = camera.getYRange();
    this.loaded = yStart / maxHeight;

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    gl.uniform1f(this.programInfo.uniformLocations.yLoaded, this.loaded);

    gl.uniform4fv(this.programInfo.uniformLocations.color, [color.red, color.green, color.blue, color.alpha]);

    {
      const offset = 0;
      gl.drawArrays(gl.TRIANGLE_STRIP, offset, 4);
    }
  };
}
